import {Spec} from 'immutability-helper';
import $ from 'jquery';
import React, {UpdateSpec} from 'react';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import ModelTypeConstants, {PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES} from 'constants/ModelTypeConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {BlobLinkPreviewFileIdConsumer} from 'notebook/data-tables/BlobLinkPreviewTabContext';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {PreviewProps} from 'notebook/notes/previews/PreviewMixin';
import {isNoteLinkedFile} from 'notebook/TypePredicates';
import {
  ExternalFileExcerpt,
  FromServerNoteLinkedFile,
  Note,
  NoteLinkedFile,
  NoteLinkedObject,
  NotePart,
  NotePartItem,
  UploadSource,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {LinkedItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import IntermediateBinder from 'ui-library/IntermediateBinder';
import * as logging from 'util/Logging';
import {capitalize, typeToName} from 'util/NameUtil';
import uniqueId from 'util/uniqueId';

import TEXT_PLUGIN from '../plugins/text-block/TextPlugin';

export const PLUGINS_BY_FILE_TYPE = {
  [ModelTypeConstants.SEQUENCE]: [
    ModelTypeConstants.ANNOTATION,
    ModelTypeConstants.PRIMER_PAIR,
    ModelTypeConstants.SEQUENCE_ANALYSIS,
    ModelTypeConstants.SEQUENCE_PRIMER,
  ],
};

export interface PreviewPluginItemProps {
  editorWidth: number;
  isSelected: boolean;
  itemId: string | null;
  enableUnlockFile: boolean;
  parentId?: string;
}

interface PreviewPlugin {
  type: string;
  typeClass?: string;
  title?: string;
  preview: React.ComponentType<PreviewProps>;
  renderFooter?: (
    editor: NoteEditor,
    item: NotePart,
    index: number,
    editorProps: NoteEditorProps,
    itemProps: PreviewPluginItemProps
  ) => JSX.Element | null;
  disableDropMessage?: string;
}

interface PreviewPluginProps {
  insertExcerptsHandler: (index: number, excerpts: Array<ExternalFileExcerpt>) => void;
  updateNoteLinkedObject: (index: number, updates: Partial<NotePartItem>) => void;
  finalizeItemWidth: (index: number, itemWidth: number) => void;
  openSequenceFeatureModal: (index: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadFile: (index: number, file: any, uploadSource?: UploadSource) => void;
  handleDropExternalFiles: (
    index: number,
    fileUploadTasks: Array<{taskId: string; fileUploadDeferred: JQueryDeferred<FromServerNoteLinkedFile>}>
  ) => void;
  handleDropBenchlingFile: (index: number, item: LinkedItem) => void;
  setUpdatedNoteLinkedFile: (index: number, newNoteLinkedFile: NoteLinkedFile) => void;
}

// Helper method for making a plugin for captioned previews.
//   Takes in {type, typeClass, title, preview, permissions, disableDropMessage}
//   where preview is a component that renders a preview from @props.item.
//   It is expected for the icon name of a preview to match the type.
export function previewPlugin(plugin: PreviewPlugin): NoteEditorPlugin & {preview: PreviewPlugin['preview']} {
  return {
    type: plugin.type,
    typeClass: plugin.typeClass,
    title: plugin.title || capitalize(typeToName(plugin.type)),
    icon: plugin.type as BenchlingIconName,
    preview: plugin.preview,
    component: MediocrePlugins.captionWrapper<NoteEditor, Note, NotePart, PreviewPluginItemProps>({
      required: ['insertExcerptsHandler', 'setAttachFeatureIndex', 'finalizeItemWidth'],
      getItemProps(item: NotePart, index: number): PreviewPluginItemProps {
        let itemId = null;
        if (item.item && 'cid' in item.item) {
          itemId = this.props.getIdFromCid(item.item.cid) ?? null;
        }
        // Acquiring a lock on a file relies on our auto-saver persisting the TFIFileMeta in the entry.
        // TODO(BNCH-2655): cidIdMap should be required + all modal-based ELN editors should be auto-saving
        // so that we can enable this functionality for all note editors.
        const enableUnlockFile = this.props.cidIdMap != null;
        return {
          editorWidth: this.state.editorWidth,
          isSelected: this.state.selected.index === index && this.state.selected.subIndex === 'item',
          itemId,
          enableUnlockFile,
        };
      },
      skipVirtualizationDueToPerformantRender(item: NotePart) {
        // Skipping since rendering an element that is primarly just an image which renders pretty quickly.
        return true;
      },
      make(
        this: NoteEditor,
        item: NotePart,
        index: number,
        editorProps: NoteEditorProps,
        itemProps: PreviewPluginItemProps,
        searchResultProps: MediocreSearchResultProps,
        resizeProps: {setItemWidth: (itemWidth: number) => void; itemWidth: number}
      ): JSX.Element {
        const content = editorProps.getContent(item);
        return (
          <IntermediateBinder<PreviewPluginProps, number>
            boundArg={index}
            insertExcerptsHandler={this.insertExcerptsHandler}
            updateNoteLinkedObject={this.updateNoteLinkedObject}
            finalizeItemWidth={this.finalizeItemWidth}
            openSequenceFeatureModal={this.openSequenceFeatureModal}
            uploadFile={this.uploadAndAttachToItem}
            handleDropExternalFiles={this.handleDropExternalFilesAsLocalUpload}
            handleDropBenchlingFile={this.replacePlaceholderWithBenchlingItem}
            setUpdatedNoteLinkedFile={this.setUpdatedNoteLinkedFile}
          >
            {(boundProps) => (
              <BlobLinkPreviewFileIdConsumer>
                {(previewFileId) => (
                  <plugin.preview
                    {...{
                      fileId: editorProps.fileId,
                      locked: editorProps.locked,
                      item: (content as Partial<NotePart>).item,
                      itemId: itemProps.itemId,
                      placeholder: item.placeholder,
                      task: item.task,
                      editorWidth: itemProps.editorWidth,
                      alignment: (content as Partial<NotePart>).alignment,
                      disableDropMessage: plugin.disableDropMessage,
                      isSelected: itemProps.isSelected,
                      permissions: editorProps.permissions,
                      enableUnlockFile: itemProps.enableUnlockFile,
                      setImageFromS3Location: this.setImageFromS3Location,
                      setNoteLinkedFileFromS3Location: this.setNoteLinkedFileFromS3Location,
                      notePartCid: item.cid,
                      previewFileId,
                      ...resizeProps,
                      ...boundProps,
                      ...editorProps.preview,
                    }}
                  />
                )}
              </BlobLinkPreviewFileIdConsumer>
            )}
          </IntermediateBinder>
        );
      },
      makeFooter(
        editor: NoteEditor,
        item: NotePart,
        index: number,
        editorProps: NoteEditorProps,
        itemProps: PreviewPluginItemProps
      ): JSX.Element | null {
        return plugin.renderFooter ? plugin.renderFooter(editor, item, index, editorProps, itemProps) : null;
      },
    }),
    linkable: true,
    alignable: true, // This plugin supports having its `alignment` set ('left', 'center', 'right')
    itemFromHTML(this: NoteEditor, $el: JQuery, item: NotePart): NotePart | null {
      // If we have a sequence attribute on the note, we can only paste into the same sequence.
      if (
        (PLUGINS_BY_FILE_TYPE[ModelTypeConstants.SEQUENCE] as Array<string>).includes(plugin.type) &&
        $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID) !== this.props.fileId
      ) {
        notifications.warn(
          `One or more notes could not be pasted: linking across sequences is currently \
not enabled.`,
          {id: 'note-editor-sequence-paste'}
        );
        return null;
      }

      // If there is a note linked object sub-item, create a cid and register it
      // using the passed in id.
      if (
        (PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES as Array<string>).includes(plugin.type) &&
        item.item != null
      ) {
        const noteLinkedObject = item.item as NoteLinkedObject;
        const noteLinkedObjectId = $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_ATTR_ID);
        if (!noteLinkedObjectId) {
          logging.error(
            `Expected ${NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_ATTR_ID} ` +
              'data attribute in clipboard HTML data'
          );
          return null;
        }
        noteLinkedObject.cid = uniqueId('note-linked-object-');
        this.props.registerObjects(_.object([noteLinkedObject.cid], [noteLinkedObjectId]));
      }

      return item;
    },

    itemToText(item: NotePart): string | null {
      return item.item && 'name' in item.item ? item.item.name : null;
    },

    itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart): JQuery {
      // If this item is a sequence attribute, leave a marker so that we can determine if pasting
      // is allowed.
      if ((PLUGINS_BY_FILE_TYPE[ModelTypeConstants.SEQUENCE] as Array<string>).includes(plugin.type)) {
        $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
      }

      // If a note linked object is being copied pass the `id` as a data attribute so
      // it can be inserted into the recipient's `cidIdMap`.
      if (
        (PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES as Array<string>).includes(plugin.type) &&
        item.item != null
      ) {
        $el.attr(
          NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_ATTR_ID,
          this.props.getIdFromCid((item.item as NoteLinkedObject).cid) ?? null
        );
      }

      // Render title
      const {text, formats, item: linkedItem, placeholder} = item;
      if (
        linkedItem &&
        isNoteLinkedFile(linkedItem) &&
        linkedItem.previewFile?.previewType === EXTERNAL_FILE_TYPE.IMAGE
      ) {
        const $imgDiv = $('<div>');
        const $img = $('<img>');
        const existingEfl = document.querySelector(`[efl_identifier="${linkedItem.previewFile.id}"]`);
        // we should already have rendered an image with base64 data
        if (existingEfl) {
          $img.attr('src', (existingEfl as HTMLImageElement).src);
        }
        $img.attr('width', item.itemWidth);
        // We put the filename as the title attribute as opposed to having an adjacent DOM node with the
        // filename to avoid poor UX when copying and pasting a single file attachment into Office.
        $img.attr('title', linkedItem.name);
        $imgDiv.append($img);
        $el.append($imgDiv);
      } else if (placeholder) {
        $el.append($('<em>').text('PLACEHOLDER'));
      } else if (linkedItem == null) {
        $el.append($('<em>').text('DELETED'));
      } else {
        const editURL = 'editURL' in linkedItem ? linkedItem.editURL : null;
        const name = 'name' in linkedItem ? linkedItem.name : '';
        const $title = editURL != null ? $('<a>').attr('href', benchlingUtil.pathToURL(editURL)) : $('<b>');
        $el.append($title.text(name));
      }

      // Render text (if non-empty)
      if (text) {
        const $text = $('<span>').html(formatters.renderHTML(text, formats ?? formatters.defaultFormats()));
        $el.append('<span>: </span>').append($text);
      }
      return $el;
    },

    getContent({
      text,
      formats,
      alignment,
      item,
      item_id, // eslint-disable-line camelcase
      placeholder,
      itemWidth,
    }: NotePart): Partial<NotePart> {
      // eslint-disable-next-line camelcase
      return {text, formats, alignment, item, item_id, placeholder, itemWidth};
    },

    // eslint-disable-next-line camelcase
    setContent({text, formats, alignment, item, item_id, placeholder, itemWidth}: NotePart): Spec<NotePart> {
      // eslint-disable-next-line camelcase
      return {$merge: {text, formats, alignment, item, item_id, placeholder, itemWidth}};
    },

    *getSearchResults(item: NotePart, matcher: TextMatcher): Generator<MediocreSearchResult> {
      // Search over the preview's caption (`item.text`)
      yield* TEXT_PLUGIN.getSearchResults!(item, matcher);
    },

    getText({text, formats}: {text: string; formats: Format}): {text: string; formats: Format} {
      return {text, formats};
    },
    setText({text, formats}: {text: string; formats: Format}): UpdateSpec {
      return {
        $merge: {text, formats},
      };
    },
  };
}
